/**
 * Turning a backend error into something safe to show a viewer.
 *
 * WHY THE COPY IS DELIBERATELY VAGUE
 *
 * A magic link is viewed by two very different people, and this app cannot tell them
 * apart: `MagicLink` calls `anonymousLogin()` unconditionally, so *every* viewer is an
 * anonymous Firebase user — including the agent's owner testing from the dashboard.
 *
 *   1. The agent's owner, testing from dashboard.aaq.live
 *   2. A stranger on the owner's own website, where the widget is embedded
 *
 * For (2), saying "out of credits" or "plan restricted" would leak our customer's
 * commercial state to *their* customer and make their brand look broken. Since we
 * cannot distinguish (1) from (2), the neutral message is the only safe option here.
 *
 * The specific, actionable message — real balance, upgrade CTA — belongs in
 * aaq-dashboard, where the viewer is known to be the account holder. See its
 * `lib/utils/apiErrorHelpers.ts`.
 *
 * The error CODE vocabulary is shared with the dashboard and the backend; only the
 * user-facing copy differs. Source of truth for the codes: aaqAppServer
 * `management_api/middlewares/payment/paymentAuthMiddleware.js`.
 */

export const API_ERROR_CODES = {
  PLAN_RESTRICTED: 'PLAN_RESTRICTED',
  AGENT_RESTRICTED: 'AGENT_RESTRICTED',
  QUOTA_LOOKUP_FAILED: 'QUOTA_LOOKUP_FAILED',
  ACCESS_CHECK_ERROR: 'ACCESS_CHECK_ERROR',
  OWNER_USERID_MISSING: 'OWNER_USERID_MISSING',
  UNAUTHORIZED: 'UNAUTHORIZED',
} as const;

/** Error thrown by the service layer when a request fails. */
export class AaqApiError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public code?: string,
    public body?: unknown,
  ) {
    super(message);
    this.name = 'AaqApiError';
  }
}

export interface ViewerFacingError {
  /** Short heading. */
  title: string;
  /** One or two sentences. Never mentions credits, plans, or billing. */
  detail: string;
  /** True when retrying might genuinely help — drives whether we offer a retry. */
  retryable: boolean;
}

/**
 * Map a failure to copy that is safe for any viewer.
 *
 * Note what is intentionally absent: the words credit, balance, plan, billing,
 * subscription, quota, and payment. If you are adding a case here, keep it that way.
 */
export function toViewerFacingError(error: unknown): ViewerFacingError {
  const code = error instanceof AaqApiError ? error.code : undefined;
  const status = error instanceof AaqApiError ? error.statusCode : 0;

  switch (code) {
    // The owner's account cannot currently place calls. The viewer may be a stranger
    // on the owner's site — say nothing about why.
    case API_ERROR_CODES.PLAN_RESTRICTED:
    case API_ERROR_CODES.AGENT_RESTRICTED:
      return {
        title: 'This assistant is unavailable right now',
        detail:
          'It has been paused temporarily. Please check back a little later, or get in touch with the site owner.',
        retryable: false,
      };

    // Genuinely transient — worth another go.
    case API_ERROR_CODES.QUOTA_LOOKUP_FAILED:
    case API_ERROR_CODES.ACCESS_CHECK_ERROR:
      return {
        title: "We couldn't start the conversation",
        detail: 'Something went wrong on our side. Please try again in a moment.',
        retryable: true,
      };

    // Misconfiguration on the agent — retrying will not help.
    case API_ERROR_CODES.OWNER_USERID_MISSING:
      return {
        title: 'This assistant is unavailable right now',
        detail: 'It is not fully set up yet. Please get in touch with the site owner.',
        retryable: false,
      };

    case API_ERROR_CODES.UNAUTHORIZED:
      return {
        title: 'Your session has expired',
        detail: 'Please refresh the page to start a new conversation.',
        retryable: true,
      };
  }

  // No code from the backend — fall back on the status.
  if (status === 0) {
    return {
      title: "We couldn't reach the assistant",
      detail: 'Please check your connection and try again.',
      retryable: true,
    };
  }

  if (status === 403 || status === 402) {
    // Unrecognised restriction-shaped status. Stay vague — this is the branch most
    // likely to be hit by a NEW backend code we have not mapped yet, so it must be
    // safe for a stranger by default.
    return {
      title: 'This assistant is unavailable right now',
      detail: 'Please check back a little later, or get in touch with the site owner.',
      retryable: false,
    };
  }

  return {
    title: "We couldn't start the conversation",
    detail: 'Something went wrong. Please try again in a moment.',
    retryable: true,
  };
}

/**
 * Read a failed fetch Response into an AaqApiError, preserving the backend's code.
 *
 * Previously the service layer did `throw new Error('Network response was not ok')`,
 * which discarded the body — that is why a restricted account produced no message at
 * all and the widget hung on "Setting up your conversation" forever.
 */
export async function errorFromResponse(response: Response): Promise<AaqApiError> {
  let body: { code?: string; message?: string } | undefined;

  try {
    body = await response.json();
  } catch {
    // Not JSON, or empty. The status alone still tells us enough.
  }

  return new AaqApiError(
    body?.message || `Request failed with status ${response.status}`,
    response.status,
    body?.code,
    body,
  );
}
